import { useState } from 'react'
import type { MeDto } from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Dialog } from './Dialog.js'
import { LanguageSwitch } from './LanguageSwitch.js'
import { useText } from '../i18n.js'

/**
 * Das eigene Konto: wer man ist und das Passwort wechseln.
 *
 * Der Wechsel beendet alle laufenden Sitzungen — auch auf anderen Geräten.
 * Das steht ausdrücklich dabei, weil es der eigentliche Zweck ist: Wer sein
 * Passwort ändert, weil ein anderer es kennt, hätte sonst nichts gewonnen.
 */
export function AccountDialog({
  me,
  onClose,
}: {
  me: MeDto
  onClose: () => void
}): React.ReactElement {
  const t = useText()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    if (newPassword !== repeat) {
      setError(t('account.mismatch'))
      return
    }
    setError(null)
    setBusy(true)
    try {
      await api.changePassword(currentPassword, newPassword)
      setDone(true)
      setCurrentPassword('')
      setNewPassword('')
      setRepeat('')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('account.changeFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog title={t('account.title')} onClose={onClose}>
      <ul className="member-list">
        <li>
          <span className="member-name">{me.displayName}</span>
          <span className="member-mail muted small">{me.email}</span>
          {me.isPlatformAdmin && <span className="badge subtle">{t('account.operator')}</span>}
        </li>
      </ul>

      {/*
        Die Sprachwahl gilt sofort am Gerät und geht zusätzlich ans Konto —
        sonst stünde die Oberfläche auf dem Bürorechner anders als unterwegs.
        Scheitert das Schreiben, bleibt es bei der örtlichen Wahl; dafür einen
        Fehler zu zeigen wäre lauter als der Vorgang wert ist.
      */}
      <div className="field">
        <span>{t('common.language')}</span>
        <LanguageSwitch onChange={(language) => void api.setLanguage(language).catch(() => {})} />
        <small>{t('account.languageHint')}</small>
      </div>

      <form className="member-add" onSubmit={submit}>
        <h4 className="dialog-section">{t('account.changePassword')}</h4>

        <label className="field">
          <span>{t('account.currentPassword')}</span>
          <input
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <label className="field">
          <span>{t('account.newPassword')}</span>
          <input
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
          <small>{t('account.minLength')}</small>
        </label>

        <label className="field">
          <span>{t('account.repeat')}</span>
          <input
            type="password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
        </label>

        <p className="muted small">{t('account.signOutHint')}</p>

        {done && <p className="muted small">{t('account.changed')}</p>}
        {error && <p className="form-error">{error}</p>}

        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('common.moment') : t('account.changePassword')}
        </button>
      </form>
    </Dialog>
  )
}
